// Node positioning and layout utilities for hierarchical graph visualization

/**
 * Calculate deterministic hierarchical positions for graph nodes
 * @param {Array} nodes - Array of node objects with hierarchical properties
 * @returns {Object} Object mapping node IDs to {x, y} positions
 */
export const calculateHierarchicalPositions = (nodes) => {
  const positions = {};
  const levelSpacing = 180; // Vertical spacing between levels
  const centerX = 0;
  const centerY = 0;

  // Group nodes by level and organize them
  const nodesByLevel = {};
  const hubNodes = [];
  const clusterNodes = [];
  const topicAreaNodes = [];
  const conversationNodes = [];

  nodes.forEach(node => {
    const level = node.level || 0;
    if (!nodesByLevel[level]) nodesByLevel[level] = [];
    nodesByLevel[level].push(node);

    if (node.isHub) hubNodes.push(node);
    else if (node.isCluster) clusterNodes.push(node);
    else if (node.isTopicArea) topicAreaNodes.push(node);
    else conversationNodes.push(node);
  });

  // Position hubs (level 0) - center them horizontally and vertically
  hubNodes.forEach((hub, index) => {
    const hubSpacing = 350; // Horizontal spacing between hubs
    positions[hub.id] = {
      x: centerX + (index - (hubNodes.length - 1) / 2) * hubSpacing,
      y: centerY // Hubs at the true center
    };
  });

  // Position clusters (level 1) - arrange above and below their parent hubs
  clusterNodes.forEach((cluster, index) => {
    const parentHub = hubNodes.find(hub => hub.topic === cluster.topic);
    const parentX = parentHub ? positions[parentHub.id].x : centerX;
    const parentY = parentHub ? positions[parentHub.id].y : centerY;
    
    // Get clusters for this hub
    const siblingClusters = clusterNodes.filter(c => c.topic === cluster.topic);
    const clusterIndex = siblingClusters.indexOf(cluster);
    const clusterSpacing = 300;
    
    // Arrange clusters horizontally above and below the hub
    const isAbove = clusterIndex % 2 === 0;
    const pairIndex = Math.floor(clusterIndex / 2);
    
    positions[cluster.id] = {
      x: parentX + (pairIndex - (Math.ceil(siblingClusters.length / 2) - 1) / 2) * clusterSpacing,
      y: parentY + (isAbove ? -levelSpacing : levelSpacing)
    };
  });

  // Position topic areas (level 2) - arrange horizontally around their parent clusters
  topicAreaNodes.forEach((area, index) => {
    const parentCluster = clusterNodes.find(cluster => 
      cluster.subtopic === area.subtopic && cluster.topic === area.topic
    );
    const parentX = parentCluster ? positions[parentCluster.id].x : centerX;
    const parentY = parentCluster ? positions[parentCluster.id].y : centerY;
    
    // Get topic areas for this cluster
    const siblingAreas = topicAreaNodes.filter(a => 
      a.subtopic === area.subtopic && a.topic === area.topic
    );
    const areaIndex = siblingAreas.indexOf(area);
    const areaSpacing = 250;
    
    // Arrange topic areas horizontally around the cluster
    positions[area.id] = {
      x: parentX + (areaIndex - (siblingAreas.length - 1) / 2) * areaSpacing,
      y: parentY + (parentY > centerY ? levelSpacing : -levelSpacing)
    };
  });

  // Position conversation nodes (level 3 or 2) - arrange in loose grid around their parents
  conversationNodes.forEach((conversation, index) => {
    let parentX = centerX;
    let parentY = centerY;
    
    if (conversation.parentArea) {
      // New deeper structure - parent is a topic area
      const parentArea = topicAreaNodes.find(area => area.id === conversation.parentArea);
      if (parentArea && positions[parentArea.id]) {
        parentX = positions[parentArea.id].x;
        parentY = positions[parentArea.id].y;
      }
    } else if (conversation.parentCluster) {
      // Legacy structure - parent is a cluster
      const parentCluster = clusterNodes.find(cluster => cluster.id === conversation.parentCluster);
      if (parentCluster && positions[parentCluster.id]) {
        parentX = positions[parentCluster.id].x;
        parentY = positions[parentCluster.id].y;
      }
    }
    
    // Get sibling conversations for the same parent
    const parentId = conversation.parentArea || conversation.parentCluster;
    const siblingConversations = conversationNodes.filter(c => 
      (c.parentArea === parentId) || (c.parentCluster === parentId)
    );
    
    const convIndex = siblingConversations.indexOf(conversation);
    const totalSiblings = siblingConversations.length;
    
    // Arrange in a loose grid pattern to minimize overlap
    const itemsPerRow = Math.ceil(Math.sqrt(totalSiblings));
    const row = Math.floor(convIndex / itemsPerRow);
    const col = convIndex % itemsPerRow;
    const spacing = 80;
    
    positions[conversation.id] = {
      x: parentX + (col - (itemsPerRow - 1) / 2) * spacing,
      y: parentY + (row - (Math.ceil(totalSiblings / itemsPerRow) - 1) / 2) * spacing
    };
  });

  return positions;
};


